// @flow
import React, { useEffect } from 'react'
import { styled } from 'styled-components'
import {
	FRAME_LOCK_ID,
	BACKGROUND_IMAGE_ID,
	CanvasMediaProvider,
} from './hooks/useCanvasMedia'
import { shapes } from './customShapes'
import { Button } from '../../../../basics/Button'
import '@tldraw/tldraw/tldraw.css'
import { useYjsStore } from './hooks/useYjsStore'

import {
	Canvas as TlCanvas,
	type Editor,
	TldrawEditor,
	defaultTools,
	DefaultFontStyle,
	DefaultHorizontalAlignStyle,
	DefaultColorThemePalette,
	TldrawUiContextProvider,
	ErrorScreen,
	useEditor,
	useValue,
	ContextMenu,
	track,
} from '@tldraw/tldraw'
import { useId } from 'react'
import ViewportLock, {
	useSnapCameraBackToOrigin,
} from './components/ViewportLock'

export type ExportData = {|
	documentId: string,
	doExport: (Editor, ids: Set<string>) => Promise<void>,
	status: 'ready' | 'pending' | 'complete',
|}

DefaultFontStyle.defaultValue = 'sans'
DefaultHorizontalAlignStyle.defaultValue = 'start'
// If any of these colors change they should also be updated in controls src/components/CreativeCanvas/Canvas.jsx
// The default yellow from tldraw is closer to a gold color, so we change it here to a truer yellow.
DefaultColorThemePalette.lightMode.yellow.solid = '#f9e432'
// There is no brown in the default palette, so we add it here, replacing light red on the color palette.
DefaultColorThemePalette.lightMode['light-red'].solid = '#7B583D'

/**
 * A canvas wrapper which provides the user identity to the CanvasWithIdentity component.
 * @returns {React$Node}
 */
export default function CreativeCanvas({
	documentId,
	canvasId,
	className,
	exporting,
	id,
}: {
	canvasId: string,
	documentId: string,
	className?: string,
	exporting?: ExportData,
	id?: string,
}): React$Node {
	return (
		<CanvasMediaProvider documentId={documentId} canvasId={canvasId}>
			<_CreativeCanvas
				exporting={exporting}
				documentId={documentId}
				className={className}
			/>
		</CanvasMediaProvider>
	)
}
/**
 * Canvas provided by TLDraw with custom toolbox UI.
 * @param {Identity} props.identity the identity used to connect the canvas to the correct yjs document.
 * @returns {React$Node}
 */
function _CreativeCanvas({
	documentId,
	className,
	exporting,
}: {
	documentId: string,
	className?: string,
	exporting?: ExportData,
}): React$Node {
	const store = useYjsStore(true, documentId, exporting)
	const onMount = editor => {
		// Overrides
		editor.setReadOnly(true)
	}
	const status = store.status

	const uniqueId = useId()
	const didErrorState = React.useState(false)
	return (
		<CanvasErrorContextProvider value={didErrorState}>
			<Container
				$readOnly
				$didError={didErrorState[0]}
				className={className}
				data-testid={`creative-canvas-document-${documentId}`}>
				<div className="canvas-positioner" id={uniqueId}>
					<TldrawEditor
						shapes={shapes}
						tools={defaultTools}
						components={{ ErrorFallback }}
						onMount={onMount}
						store={store}>
						<CustomUI id={uniqueId} status={status} />
						{exporting && status === 'synced-remote' && (
							<Exporter {...exporting} />
						)}
					</TldrawEditor>
				</div>
			</Container>
		</CanvasErrorContextProvider>
	)
}

const Exporter = track((props: ExportData) => {
	const editor = useEditor()
	const ids = editor.currentPageShapeIds
	React.useEffect(() => {
		if (props.status === 'ready' && ids.size) {
			props.doExport(editor, ids)
		}
	}, [props, ids, editor])

	return null
})

function CustomUI({ id, status }: { id: string, status: string }): React$Node {
	return (
		<TldrawUiContextProvider>
			<ViewportLock id={id} status={status} />
			<BackToContent id={id} />
			<ContextMenu>
				<TlCanvas />
			</ContextMenu>
		</TldrawUiContextProvider>
	)
}

/**
 * A component which displays a button for students to clearly see how to return to the main view of the canvas content.
 * The button will appear after a student zooms in / zooms out or pans across the canvas.
 * @param {string} props.documentId needed in order to snap the camera back to the origin. This is the css selector id of the main tldraw component.
 * @returns { React$Node }
 */
function BackToContent({ id }: { id: string }) {
	const editor = useEditor()
	const snapCameraBackToOrigin = useSnapCameraBackToOrigin(editor, id)

	const isNotLockedWithFrame = useValue(
		'is-camera-not-locked-with-frame',
		() => {
			const camera = editor.camera
			if (camera.x !== 0 || camera.y !== 0) {
				return true
			}
			return false
		},
		[editor]
	)

	return (
		isNotLockedWithFrame && (
			<BackButton onClick={snapCameraBackToOrigin}>
				Back to <br />
				Main View
			</BackButton>
		)
	)
}

const Container = styled.div`
	--border-radius: 24px;
	--border-width: 8px;
	--inner-border-radius: calc(var(--border-radius) - var(--border-width));

	--layer-canvas: 0;
	--layer-menus: 1;
	--layer-overlays: 2;

	background-color: ${({ theme }) => theme.primaryDark};

	border: var(--border-width) solid ${({ theme }) => theme.primaryDark};
	border-radius: var(--border-radius);

	.tl-container {
		border-radius: var(--inner-border-radius);
		${({ $readOnly, $didError }) =>
			// Allow pointer events if there was an error so the user can access the link inside the error fallback component
			$readOnly && !$didError && ` & * { pointer-events: none !important;}`}
	}

	.canvas-positioner {
		position: relative;
		z-index: 0;
		aspect-ratio: 4 / 3;
	}

	#${FRAME_LOCK_ID} *,
	#${BACKGROUND_IMAGE_ID} * {
		pointer-events: none !important;
	}
`

const BackButton = styled(Button)`
	position: absolute;
	left: 16px;
	top: 8px;
	z-index: var(--layer-overlays);
`

/* *************************
 ** Canvas Error Handling **
 ***************************/

type CanvasErrorContextValue = [
	boolean,
	(didError: boolean | ((currentState: boolean) => boolean)) => void
]
const CanvasErrorContext = React.createContext()

function useCanvasErrorContext(): CanvasErrorContextValue {
	const value = React.useContext(CanvasErrorContext)
	if (!value) {
		throw new Error(
			'useCanvasErrorContext must be used within a CanvasErrorContextProvider'
		)
	}
	return value
}

function CanvasErrorContextProvider({
	children,
	value,
}: {
	children: React$Node,
	value: CanvasErrorContextValue,
}) {
	return (
		<CanvasErrorContext.Provider value={value}>
			{children}
		</CanvasErrorContext.Provider>
	)
}

/**
 * A component that will be shown in the creative canvas when an error occurs.
 */
function ErrorFallback({ error }: { error: Error }): React$Node {
	const [, setDidError] = useCanvasErrorContext()
	useEffect(() => {
		setDidError(true)
	}, [setDidError])

	return (
		<ErrorScreen error={error}>
			<p className="text-2xl text-center">
				There was an unexpected error displaying the Canvas. Please refresh the
				page to try again.
			</p>
			<Button
				onClick={() => {
					window.location.reload()
				}}>
				Refresh
			</Button>
		</ErrorScreen>
	)
}
